import React, { useEffect, useRef, useState } from 'react';
import { loadTraderCard } from '../../lib/traderCardData';
import { drawTraderCard, loadImage, canvasToPngBlob } from '../../lib/traderCardCanvas';

// «بطاقة تاجر» for an unclaimed catalogue factory (developer handoff of
// 2026-10-17, item 1). Builds the PNG and the Chinese message; the admin sends
// them by hand. Shows the trader's first name only (owner, 2026-10-17).
export default function TraderCardModal({ factoryId, trader, isAr, onClose, flash }) {
  const canvasRef = useRef(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    let alive = true;
    loadTraderCard({ factoryId, trader })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => {
        console.error('[TraderCardModal] load failed:', e?.message || e);
        if (alive) setErr(e?.message || 'load failed');
      });
    return () => { alive = false; };
  }, [factoryId, trader]);

  useEffect(() => {
    if (!data || !canvasRef.current) return undefined;
    let alive = true;
    Promise.all([loadImage('/email-logo.png'), loadImage(data.content.image)]).then(([logo, image]) => {
      if (!alive || !canvasRef.current) return;
      try {
        drawTraderCard(canvasRef.current, data.content, { logo, image });
        setDrawn(true);
      } catch (e) {
        console.error('[TraderCardModal] draw failed:', e?.message || e);
        setErr(e?.message || 'draw failed');
      }
    });
    return () => { alive = false; };
  }, [data]);

  const say = (ar, en) => (flash ? flash(isAr ? ar : en) : null);

  const download = async () => {
    const blob = canvasRef.current ? await canvasToPngBlob(canvasRef.current) : null;
    if (!blob) { say('تعذّر إنشاء الصورة', 'Could not create the image'); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `maabar-trader-card-${data?.factory?.claim_slug || 'factory'}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.message);
      say('نُسخ النص الصيني مع الرابط', 'Chinese text + link copied');
    } catch (e) {
      console.error('[TraderCardModal] copy failed:', e?.message || e);
      say('تعذّر النسخ — انسخه من المربع', 'Copy failed — copy it from the box');
    }
  };

  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
  const facName = data?.factory ? (data.factory.company_name_latin || data.factory.company_name) : '';

  return (
    <div className="ac-modal-ov" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ac-modal" style={{ maxWidth: 560, fontFamily: font }} onClick={(e) => e.stopPropagation()}>
        <h2 className="ac-modal-title">{isAr ? 'بطاقة تاجر للمصنع' : 'Trader card for the factory'}{facName ? ` — ${facName}` : ''}</h2>
        <p className="ac-sub" style={{ marginTop: -8, marginBottom: 12 }}>
          {isAr
            ? 'تُرسل يدويًا للمصنع ليستلم صفحته ويرد على التاجر في معبر. تظهر فيها: الاسم الأول للتاجر فقط، سؤاله، المنتج، ورابط الاستلام.'
            : 'Sent by hand so the factory claims its page and answers on Maabar. Shows only the trader’s first name, the question, the product and the claim link.'}
        </p>

        {err && <p style={{ color: 'var(--ac-danger)', fontSize: 13 }}>{err}</p>}
        {!data && !err && <div className="ac-skel" style={{ height: 300 }} />}

        {data && (
          <>
            {data.factory.claimed_at && (
              <p style={{ color: 'var(--ac-warn)', fontSize: 13 }}>{isAr ? 'هذا المصنع استلم صفحته — لا حاجة للبطاقة.' : 'This factory has already claimed its page.'}</p>
            )}
            {!data.content.link && (
              <p style={{ color: 'var(--ac-danger)', fontSize: 13 }}>{isAr ? 'لا يوجد رابط استلام (claim_slug) لهذا المصنع.' : 'This factory has no claim link (claim_slug).'}</p>
            )}
            {!data.hasProduct && (
              <p style={{ color: 'var(--ac-warn)', fontSize: 13 }}>{isAr ? 'المحادثة لا تذكر منتجًا محددًا.' : 'The conversation names no product.'}</p>
            )}
            {data.translationFailed && (
              <p style={{ color: 'var(--ac-warn)', fontSize: 13 }}>{isAr ? 'تعذّرت الترجمة الصينية — البطاقة بدون سطر الترجمة.' : 'Chinese translation failed — the card has no translation line.'}</p>
            )}

            <canvas ref={canvasRef} data-testid="trader-card-canvas"
              style={{ width: '100%', height: 'auto', borderRadius: 12, border: '1px solid var(--ac-line)', display: drawn ? 'block' : 'none' }} />
            {!drawn && <div className="ac-skel" style={{ height: 300 }} />}

            <label style={{ display: 'block', fontSize: 12, color: 'var(--ac-faint)', margin: '14px 0 4px' }}>
              {isAr ? 'النص الذي يُرسل (صيني)' : 'Message to send (Chinese)'}
            </label>
            <textarea className="ac-input" readOnly value={data.message} dir="auto"
              style={{ width: '100%', minHeight: 150, fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit' }} />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button className="ac-btn ac-btn-sm" onClick={onClose}>{isAr ? 'إغلاق' : 'Close'}</button>
              <button className="ac-btn ac-btn-sm" disabled={!drawn} onClick={download}>{isAr ? 'تحميل PNG' : 'Download PNG'}</button>
              <button className="ac-btn ac-btn-sm" onClick={copy}>{isAr ? 'نسخ النص الصيني مع الرابط' : 'Copy Chinese text + link'}</button>
              {data.waUrl ? (
                <a className="ac-btn ac-btn-sm ac-btn-primary" href={data.waUrl} target="_blank" rel="noopener noreferrer">
                  {isAr ? 'فتح واتساب' : 'Open WhatsApp'}
                </a>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--ac-faint)', alignSelf: 'center' }}>
                  {isAr ? 'لا يوجد رقم واتساب لهذا المصنع' : 'No WhatsApp number on file'}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
